import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";
import { useAuth } from "./useAuth";

/**
 * @typedef {object} DeliveryUpsellPagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} DeliveryUpsellFilter
 * @property {string} search
 */

/**
 * @typedef {object} DeliveryUpsellSorting
 * @property {string} orderBy
 * @property {string} order
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: DeliveryUpsellFilter, pagination: DeliveryUpsellPagination, sorting: DeliveryUpsellSorting}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("deliveryUpsellFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 20,
    },
    sorting: {
      orderBy: "created_at",
      order: "desc",
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      const finalState = {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
        sorting: { ...defaults.sorting, ...(savedState.sorting || {}) },
      };
      return finalState;
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "deliveryUpsellFilter": ${error}`
      );
      return defaults;
    }
  }
  return defaults;
};

export function useDeliveryUpsell() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [auxSearcher, setAuxSearcher] = useState(filters.search);
  const [deliveryUpsells, setDeliveryUpsells] = useState([]);
  const [deliveryUpsellsCount, setDeliveryUpsellsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    setAuxSearcher(value);
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSortChange = useCallback((orderBy) => {
    setSorting((previous) => ({
      orderBy: orderBy,
      order:
        previous.orderBy === orderBy && previous.order === "asc"
          ? "desc"
          : "asc",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const fetchDeliveryUpsells = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };
    try {
      const {
        data: { results: deliveryUpsells, count },
      } = await axios.get("upsellings/partnerships/incoming/", {
        params,
      });
      setDeliveryUpsells(deliveryUpsells || []);
      setDeliveryUpsellsCount(count || 0);
    } catch (error) {
      console.error(`No se pudo obtener los envíos asignados: ${error}`);
      setError("No se pudo obtener los envíos asignados");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, sorting]);

  const fetchDeliveryUpsellsByStatus = useCallback(async (status = "PENDING") => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { delivery_upsells: deliveryUpsells, count },
      } = await axios.get(`upsellings/partnerships/delivery_upsells/`, { params: { status } });
      setDeliveryUpsells(deliveryUpsells || []);
      setDeliveryUpsellsCount(count || 0);
    } catch (error) {
      console.error(`No se pudo obtener los envíos asignados: ${error}`);
      setError("No se pudo obtener los envíos asignados");
    } finally {
      setLoading(false);
    }
  }, []);

  const manageRequests = useCallback(
    async (order_ids = [], action = "ACCEPT") => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("upsellings/partnerships/manage_requests/", { order_ids, action });
        fetchDeliveryUpsells();
      } catch (error) {
        console.error(`Error al administrar la solicitud: ${error}`);
        setError("Error al administrar la solicitud");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchDeliveryUpsells]
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (auxSearcher !== filters.search) {
        handleFilterChange("search", auxSearcher);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [auxSearcher, filters.search, handleFilterChange]);

  useEffect(() => {
    localStorage.setItem(
      "deliveryUpsellFilter",
      JSON.stringify({
        ...filters,
        ...pagination,
      })
    );
  }, [filters, pagination]);

  // PUBLIC API OF THE HOOK
  return {
    // Data
    deliveryUpsells,
    deliveryUpsellsCount,

    // UI State
    loading,
    error,

    // Control State
    pagination,
    filters: { ...filters, search: auxSearcher },
    sorting,

    // Actions
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    handleSearchChange,

    // API Calls
    fetchDeliveryUpsells,
    fetchDeliveryUpsellsByStatus,
    manageRequests,
  };
}
